import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import GlobalError from '../components/GlobalError';
import { useSnackbar } from '../contexts/SnackbarProvider';
import axiosInstance from '../utils/axiosInstance';
import { formatDate, formatCurrency } from '../utils/format';
import { useForm } from 'react-hook-form';
import { calculateTotalAmount, calculateItemSubtotal, calculateSellingPriceIncludingTax } from '../utils/function';
import TableView from '../components/TableView';
import LabelValueItem from '../components/LabelValueItem';
import OrderClientInfo from '../components/OrderClientInfo';
import useOrderTable from '../hooks/useOrderTable';

import {
  Grid, Box, Typography, Button, Tooltip, TextField,
  Stack, Link, Divider, Paper, IconButton, Alert
} from '@mui/material';
import { SupervisorAccount, Sync, Edit, Close, Check, HelpOutline, Download } from '@mui/icons-material';
const InvoiceDetailPage = ({ hasLimit }) => {
  const { showSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedClient, setSelectedClient] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({
    items: []
  });
  const { register, handleSubmit, control, trigger, getValues, formState: { errors }, setValue, reset } = useForm({
    defaultValues: orderData,
  });
  const handleItemDelete = async (index, invoice_item_id) => {
    const values = getValues();

    // 新規行（バリデーション・confirmなし）
    if (!invoice_item_id) {
      const updatedItems = values.items.filter((_, i) => i !== index);

      const { total_amount, total_amount_including_tax } =
        calculateTotalAmount(updatedItems);

      setOrderData(prev => ({
        ...prev,
        items: updatedItems,
        total_amount,
        total_amount_including_tax,
      }));

      setValue('items', updatedItems);
      return;
    }

    // バリデーション実行
    const valid = await trigger(); // 全フィールドをチェック
    if (!valid) {
      showSnackbar('入力内容に不備があります。削除できません', 'error');
      return;
    }

    const ok = window.confirm(
      'この明細を削除します。よろしいですか？\n\n' +
      '※ 請求を取り消す場合は「請求キャンセル」ボタンを使用してください。'
    );
    if (!ok) return;

    try {

      // 削除対象を除いた items 配列を作る
      const updatedItems = values.items.filter(item => item.invoice_item_id !== invoice_item_id);
      // 合計計算
      const { total_amount, total_amount_including_tax } = calculateTotalAmount(updatedItems);
      // サーバー送信用データ
      const submitData = {
        ...orderData,
        items: updatedItems,
        total_amount,
        total_amount_including_tax,
        delete_item_id: invoice_item_id,
      };

      // DB 更新
      const res = await axiosInstance.put(`/invoice/${id}/update`, { orderData: submitData });

      showSnackbar(res?.data?.message || '請求アイテムを削除しました', 'success');

      fetchOrder();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error(err);
      showSnackbar(err?.response?.data?.message || 'この請求は確定済みのため、削除できません', 'error');
    }
  };
  const {
    columns,
    updateItem,
    handleItemChange,
  } = useOrderTable({
    control,
    errors,
    //isEditMode,
    hasLimit,
    axiosInstance,
    selectedClient,
    orderData,
    setOrderData,
    calculateSellingPriceIncludingTax,
    calculateItemSubtotal,
    calculateTotalAmount,
    formatCurrency,
    handleItemDelete
  });
  const fetchOrder = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/invoice/${id}`);
      setOrderData(res.data);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
      setError(err?.response?.data?.message || '取得に失敗しました');
      setOrderData(null);
    }
  }, [id, showSnackbar]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  useEffect(() => {
    if (!orderData) return;

    const formattedItems = (orderData.items || []).map(item => ({
      ...item,
      price: Math.floor(item.price),          // 定価を整数化
      tax_rate: Math.floor(item.tax_rate),    // 税率も整数
    }));

    reset({
      ...orderData,
      items: formattedItems,
      delivery_date: orderData.delivery_date
        ? formatDate(orderData.delivery_date)
        : '',
      payment_due_date: orderData.payment_due_date
        ? formatDate(orderData.payment_due_date)
        : '',
      period_from: orderData.period_from
        ? formatDate(orderData.period_from)
        : '',
      period_to: orderData.period_to
        ? formatDate(orderData.period_to)
        : '',
    });
  }, [orderData, reset]);

  useEffect(() => {
    const snackbarState = location.state?.snackbar;
    if (snackbarState) {
      showSnackbar(snackbarState.message, snackbarState.severity);
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, showSnackbar]);

  if (!orderData) return null;

  const onSubmit = async (data) => {
    const submitOrderData = {
      ...orderData,
      ...data,
      items: orderData.items,
      total_amount: orderData.total_amount,
      total_amount_including_tax: orderData.total_amount_including_tax
    };
    try {
      const res = await axiosInstance.put(`/invoice/${id}/update`, { orderData: submitOrderData });
      showSnackbar(res?.data?.message || '請求情報が更新されました', 'success');
      fetchOrder();
      setIsEditMode(false);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
      showSnackbar(err?.response?.data?.message || '更新に失敗しました', 'error');
    }
  };

  const handleStatusChange = async ({
    status,
    confirmMessage,
    successMessage,
    navigateTo,
  }) => {
    if (confirmMessage && !window.confirm(confirmMessage)) return;

    try {
      const res = await axiosInstance.post('/invoice/update-status', {
        Ids: [id],
        status,
      });
      if (successMessage) {
        showSnackbar(res?.data?.message || '請求情報が更新されました', 'success');
      }
      if (navigateTo) navigate(navigateTo);

    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error(err);
      }
      showSnackbar(err?.response?.data?.message || '更新に失敗しました', 'error');
    } finally {
      fetchOrder();
    }
  };
  const handleIssueSingle = async (invoiceId) => {
    if (isEditMode || !id || downloading) return;
    setDownloading(true);
    try {
      const res = await axiosInstance.post('/invoice/issued',
        { invoice_id: invoiceId },
        { responseType: 'blob' }
      );

      let filename = `invoice_${invoiceId}.pdf`;
      const disposition = res.headers['content-disposition'];
      if (disposition) {
        const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
        if (utf8Match?.[1]) {
          filename = decodeURIComponent(utf8Match[1]);
        } else {
          const normalMatch = disposition.match(/filename="?([^"]+)"?/i);
          if (normalMatch?.[1]) { filename = normalMatch[1]; }
        }
      }

      const blob = new Blob(
        [res.data],
        { type: 'application/pdf' }
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
      showSnackbar('請求書をダウンロードしました', 'success');
      fetchOrder();
    } catch (err) {
      console.error(err);
      showSnackbar(err?.response?.data?.message || '発行に失敗しました', 'error');
    } finally {
      setDownloading(false);
    }
  };
  if (error) return <GlobalError errorMessage={error} />;
  return (
    <>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Typography variant="h1" component="h1">請求詳細</Typography>
        <div className='button-row'>
          {orderData.invoice_status_id === '1' && (
            <Button
              variant="outlined"
              color="error"
              onClick={async () => {
                if (!window.confirm('請求書を削除してやり直しますか？')) return;

                await axiosInstance.delete(`/invoice/${orderData.invoice_id}`);
                navigate('/invoices', { state: { snackbar: { message: '請求を削除しました。新たに請求を作成してください', severity: 'success' } } });
              }}
            >
              作成をやり直す
            </Button>
          )}
        </div>
      </Stack>
      <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Paper variant='section'>
          {orderData.client_id ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              取引先情報と紐づいています
            </Alert>) : (
            <Alert severity="warning" sx={{ mb: 2 }}>
              取引先情報と紐づいていません。
            </Alert>
          )}
          {orderData.invoice_status_id === '3' && (
            <Alert severity="success" sx={{ mb: 2 }}>
              入金済み
            </Alert>
          )}
          {orderData.invoice_status_id === '4' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              請求対象外
            </Alert>
          )}
          {orderData.invoice_status_id === '2' &&
            <Alert
              severity="info"
              sx={{ mb: 2 }}
            >
              請求済み
              <Link
                component="button"
                onClick={() => {
                  if (!window.confirm('請求ステータスを未請求に戻しますか？')) return;
                  handleStatusChange({
                    status: 1,
                    successMessage: 'ステータスを変更しました',
                  })
                }}
                underline="none"
                sx={{ ml: 2 }}
              >
                （未請求に戻す）
              </Link>
            </Alert>
          }
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><LabelValueItem label='請求日時' value={formatDate(orderData.invoice_date)} /></Grid>
            <Grid item xs={12} sm={6}><LabelValueItem label='請求番号' value={orderData.invoice_number} /></Grid>
          </Grid>
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" mt={2} mb={1}>
            <TextField
              label="請求期間"
              type="date"
              name="period_from"
              disabled={!isEditMode}
              error={!!errors.period_from}
              helperText={errors.period_from?.message}
              {...register('period_from',
              )}
            />
            <Typography>～</Typography>
            <TextField
              type="date"
              name="period_to"
              disabled={!isEditMode}
              error={!!errors.period_to}
              helperText={errors.period_to?.message}
              {...register('period_to',
              )}
            />
          </Stack>
          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
            <Typography component='h2' variant="h4" pb={1}>ご請求先および支払い情報</Typography>
            {hasLimit('max_clients') && orderData.client_id && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<SupervisorAccount />}
                onClick={() => navigate(`/clients/${orderData.client_id}`)}
              >
                顧客情報を確認
              </Button>
            )}
          </Stack>
          <OrderClientInfo
            hasLimit={hasLimit}
            isEditMode={isEditMode}
            register={register}
            control={control}
            setValue={setValue}
            errors={errors}
            fieldErrors={fieldErrors}
            setFieldErrors={setFieldErrors}
            mode='invoice'
          />
          <Divider sx={{ my: 2 }} />
          {/* 連絡事項（注文メモ） */}
          <TextField
            label="備考"
            multiline
            minRows={3}
            disabled={!isEditMode}
            error={!!errors.notes}
            helperText={errors.notes?.message}
            {...register('notes', {
              maxLength: {
                value: 5000,
                message: '5000文字以内で入力してください',
              },
            })}
            inputProps={{ maxLength: 5000 }}
            sx={{ mt: 2 }}
          />
          <Divider sx={{ my: 2 }} />
          <Typography component='h2' variant="h4">商品情報</Typography>
          <Alert severity="info">
            各種項目の削除を行うと、削除した項目は締め請求、都度請求画面で再処理が必要です。
          </Alert>
          <TableView dataset={orderData.items ?? []} columns={columns} />
          {/*<Button
            type="button"
            variant="outlined"
            startIcon={<Add />}
            onClick={addItem}
            disabled={!isEditMode}
            size='small'
            sx={{ mt: 1 }}
          >
            アイテムを追加
          </Button>*/}
          {/* 合計金額 */}
          <Box sx={{ maxWidth: 400, ml: 'auto', mt: 2 }}>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography fontWeight="bold">合計金額（税抜）</Typography>
                <Typography fontWeight="bold">{formatCurrency(orderData.total_amount)}</Typography>
              </Stack>
              <Divider />
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography fontWeight="bold">合計金額（税込）</Typography>
                <Typography fontSize={22} fontWeight="bold">{formatCurrency(orderData.total_amount_including_tax)}</Typography>
              </Stack>
              <Divider />
            </Stack>
          </Box>

          <div className='button-row'>
            {['1'].includes(orderData.invoice_status_id) && (
              <>
                <Button
                  variant='outlined'
                  startIcon={isEditMode ? <Close /> : <Edit />}
                  onClick={async () => {
                    if (isEditMode) {
                      await fetchOrder();
                    }
                    setIsEditMode(prev => !prev);
                  }}
                >
                  {isEditMode ? '編集を破棄する' : 'この請求を編集'}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<Sync />}
                  disabled={!isEditMode}
                >
                  内容を更新する
                </Button>
              </>
            )}
          </div>
          <Divider sx={{ my: 4 }} />
          {hasLimit('invoices_and_delivery') && ['1', '2', '3'].includes(orderData.invoice_status_id) && (
            <Alert severity="info" icon={<HelpOutline />}>
              帳票に記載される自社情報は「会社設定」で設定できます。
              <Link
                component="button"
                onClick={() => navigate('/setting/invoice')}
                underline="none"
                sx={{ ml: 2 }}
              >
                （会社設定）
              </Link>
            </Alert>
          )}
          <div className='button-row'>
            {/* 請求書 / 納品書 */}
            {['1', '2'].includes(orderData.invoice_status_id) && (
              <Button
                type="button"
                disabled={isEditMode || !id || downloading}
                onClick={() => handleIssueSingle(id)}
                variant={orderData.invoice_status_id === '1' ? "contained" : "outlined"}
                endIcon={<Download />}
              >
                {downloading ? 'ダウンロード中…' : `請求書のダウンロード`}
              </Button>
            )}
            {orderData.invoice_status_id === '2' && (
              <Button
                variant="contained"
                onClick={() =>
                  handleStatusChange({
                    status: 3,
                    successMessage: '入金済みステータスへ変更しました',
                  })
                }
                startIcon={<Check />}
              >
                入金済みにする
              </Button>
            )}
            {['1', '2', '3'].includes(orderData.invoice_status_id) && (
              <Button
                variant="outlined"
                color="error"
                onClick={() =>
                  handleStatusChange({
                    status: 4, // キャンセルステータス
                    confirmMessage: '請求対象外にします。よろしいですか？',
                    successMessage: '請求対象外にしました。',
                    navigateTo: '/invoices', // 成功後に移動する場合
                  })
                }
                sx={{ ml: 'auto' }}
              >
                請求対象外にする
              </Button>
            )}
          </div>
        </Paper >
      </form>
    </>
  );
};

export default InvoiceDetailPage;
